import { ArrowTurnDownLeftIcon } from '@systemeio/ui/icons'
import { ContactSlugEnum, DatePicker, FormInputSearch, FormSelect, Hint, PrimaryButton, Tooltip, getDate, toast } from '@systemeio/ui'
import { UnprocessableContentError } from '@systemeio/util'
import { ContactsLoco } from 'modules/contacts/constants/contacts-loco'
import { defaultFilters } from 'modules/contacts/contacts-bin/constants/constants'
import { ContactFilterRemovedReasonLocoKeys } from 'modules/contacts/contacts-bin/constants/contacts-bin-filter-titles'
import { useContactsBulkRestore } from 'modules/contacts/contacts-bin/hooks/use-contacts-bulk-restore'
import { useContactsRemovedList } from 'modules/contacts/contacts-bin/hooks/use-contacts-removed-list'
import {
  ContactFilterRemovedDataInterface,
  ContactRemovedInterface,
} from 'modules/contacts/contacts-bin/types/contact-removed-interface'
import { getBreadCrumbsData } from 'modules/contacts/contacts-bin/utils/get-bread-crumbs-data'
import {
  ContactFilterRemovedReasonTypeEnum,
  ContactSearchFieldEnum,
} from 'modules/contacts/enums/contact-enum'
import { useApplyFilterWithConditions } from 'modules/contacts/hooks/use-apply-filter-with-conditions'
import { useContactFilterPagination } from 'modules/contacts/hooks/use-contact-filter-pagination'
import { useFilterWithConditions } from 'modules/contacts/hooks/use-filter-with-conditions'
import {
  ContactFilterDateInterface,
  ContactFilterRemovedReasonInterface,
  ContactFilterTextInterface,
} from 'modules/contacts/types/contact-filter-interface'
import { ContactBulkInterface } from 'modules/contacts/types/contact-menu-interface'
import { NextPage } from 'next'
import React, { useEffect, useState } from 'react'
import BreadcrumbsDashboard from 'shared/components/breadcrumbs'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useListingSelect } from 'shared/hooks/use-listing-select'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { dateTimeFormat } from 'shared/utils/date-time-format'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'
import { getToday } from 'shared/utils/get-dates'
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules'
import { RequestModuleEnum } from 'shared/enums/request-module-enum'
import { OrderEnum } from 'shared/types/pagination-interface'

const Contacts: NextPage = () => {
  const { user, isRootAdmin, intlLocale } = useUser()
  const { t } = useLocoTranslation()
  const { contactsBulkRestore, isFetching } = useContactsBulkRestore()
  const { revalidateModules } = useRevalidateModules()

  const [filterData, setFilterData] = useState<ContactBulkInterface>({
    contactIds: [],
    conditions: [],
    type: 'all-filtered-contacts',
  })

  const { setConditions, conditions, filter, setFilter, filterFields, filtersReady } =
    useFilterWithConditions(defaultFilters)

  const { setPagination, pagination, setLimit, limit } = useContactFilterPagination({
    conditions,
    setConditions,
  })

  const { removedContacts, abort } = useContactsRemovedList({ limit, pagination, conditions })

  useApplyFilterWithConditions({
    abort,
    filterFields,
    filtersReady,
    setLimit,
    setConditions,
    conditions,
    filter,
    onFilterChange: () => setPagination({ startFromId: undefined, order: OrderEnum.next }),
    delay: 1,
  })

  const { renderSelectButton, getListingSelect, renderSelectAllButton } = useListingSelect({
    listing: removedContacts,
    filter: conditions,
  })
  const { selectedAll, selectedIds, exceptionIds } = getListingSelect()

  const ContactRemovedTypeDataForRootAdmin: ContactFilterRemovedDataInterface[] = Object.values(
    ContactFilterRemovedReasonTypeEnum,
  ).map(el => ({ id: el, caption: t(ContactFilterRemovedReasonLocoKeys[el]) }))

  const ContactRemovedTypeDataForUser = ContactRemovedTypeDataForRootAdmin.filter(
    item => item.id !== ContactFilterRemovedReasonTypeEnum.automatically_from_sio_students,
  )

  const onContactsBulkRestore = async (data: ContactBulkInterface) => {
    try {
      await contactsBulkRestore(data)
      await revalidateModules([RequestModuleEnum.Contacts], true)
      toast.success(t('dashboard.contacts_bin.toast_contact_restored'))
    } catch (e) {
      if (e instanceof UnprocessableContentError) {
        e.errors.violations?.forEach(violation => {
          toast.error(violation.message)
        })
      }
    }
  }

  useEffect(() => {
    setFilterData({
      contactIds: selectedAll ? Array.from(exceptionIds) : Array.from(selectedIds),
      type: selectedAll ? 'all-filtered-contacts' : 'only-selected-contacts',
      conditions: conditions,
    })
  }, [selectedAll, selectedIds, conditions, exceptionIds])

  const columns: ColumnDefinitionInterface<ContactRemovedInterface>[] = [
    {
      key: 'registeredAt',
      header: (
        <div className={`flex ${removedContacts && 'gap-3'}`}>
          <div className="hidden items-center lg:flex">
            {removedContacts && removedContacts.items.length !== 0 && renderSelectAllButton()}
          </div>
          <Tooltip
            wrapperClassName={'w-full'}
            label={t('dashboard.contacts_bin.removal_date')}
            mode={'overflow'}
          >
            {t('dashboard.contacts_bin.removal_date')}
          </Tooltip>
        </div>
      ),
      onRender: contact => (
        <div className="flex gap-3">
          <div className="flex items-center">{renderSelectButton(contact)}</div>
          <span>
            {dateTimeFormat(contact.removedAt, {
              timeFormat: user?.timeFormat,
              dateFormat: user?.dateFormat,
              timeZone: user?.timezone,
            })}
          </span>
        </div>
      ),
      widthPercent: 20,
    },
    {
      key: 'email',
      header: t('global.email_address'),
      widthPercent: 35,
      onRender: contact => (
        <div className={`flex w-full items-center gap-2 overflow-hidden`}>
          <div className={'h-[48px] w-[48px] min-w-[48px] rounded-full'}>
            <img className={'rounded-full'} src={contact.avatar} alt={'avatar'} />
          </div>
          <div className="flex w-fit flex-col justify-center overflow-hidden">{contact.email}</div>
        </div>
      ),
    },
    {
      key: 'removedReasonType',
      header: t('dashboard.contacts_bin.removed_reason_type'),
      widthPercent: 25,
      onRender: contact =>
        contact.removedReasonType && (
          <span>{t(ContactFilterRemovedReasonLocoKeys[contact.removedReasonType])}</span>
        ),
    },
    {
      key: 'expirationDate',
      header: (
        <div className={`flex items-center gap-1`}>
          <Tooltip
            wrapperClassName={'truncate'}
            mode={'overflow'}
            label={t('dashboard.contacts_bin.expiration_date')}
          >
            {t('dashboard.contacts_bin.expiration_date')}
          </Tooltip>
          <Hint className="truncate pr-3" label={t('dashboard.contacts_bin.hint_expiration_at')} />
        </div>
      ),
      widthPercent: 20,
      onRender: contact => (
        <span>
          {contact.expirationAt
            ? dateTimeFormat(contact.expirationAt, {
                timeFormat: user?.timeFormat,
                dateFormat: user?.dateFormat,
                timeZone: user?.timezone,
              })
            : t('dashboard.contacts_bin.not_applicable')}
        </span>
      ),
    },
  ]

  return (
    <ListingLayout
      data={removedContacts}
      counter={false}
      type={'side'}
      title={<BreadcrumbsDashboard data={getBreadCrumbsData(t)} />}
      customHeaderElement={
        <PrimaryButton isFetching={isFetching} onClick={() => onContactsBulkRestore(filterData)}>
          <ArrowTurnDownLeftIcon />
          <span className="hidden lg:flex">{t('dashboard.contacts_bin.restore')}</span>
        </PrimaryButton>
      }
      filterData={{
        limiter: { limit, setLimit },
        paginator: { pagination, setPagination },
        filterer: {
          filter,
          setFilter,
          defaultFilters,
        },
        filterable: [
          {
            field: ContactSlugEnum.Email,
            onRender: (value, onChange) => {
              const email = value[0] as ContactFilterTextInterface
              return (
                <FormInputSearch
                  label={
                    <span className={'font-inter text-text-primary text-sm font-bold'}>
                      {t('global.email')}
                    </span>
                  }
                  key={'email-filter'}
                  type={'email'}
                  value={email.searchValue}
                  onClear={() =>
                    onChange([{ compareOperation: email.compareOperation, searchValue: '' }])
                  }
                  onChange={e =>
                    onChange([
                      { compareOperation: email.compareOperation, searchValue: e.target.value },
                    ])
                  }
                  placeholder={t('global.email')}
                />
              )
            },
          },
          {
            field: ContactSearchFieldEnum.removed_at,
            onRender: (value, onChange) => {
              const removedAt = value[0] as ContactFilterDateInterface
              return (
                <div key={'removed_at-filter'} className={'flex flex-col gap-1'}>
                  <span className={'font-inter text-text-primary text-sm font-bold'}>
                    {t('dashboard.contacts_bin.removed_at')}
                  </span>
                  <DatePicker
                    maxDate={getToday(user?.timezone)}
                    minDate={getDate(user?.createdAt, user?.timezone)}
                    value={removedAt.searchValue}
                    onChange={date =>
                      onChange([
                        { compareOperation: removedAt.compareOperation, searchValue: date },
                      ])
                    }
                    timezone={user?.timezone}
                    intlLocale={intlLocale}
                    createdAt={user?.createdAt}
                  />
                </div>
              )
            },
          },
          {
            field: ContactSearchFieldEnum.removed_reason_type,
            onRender: (value, onChange) => {
              const contactRemovedReasonType = value[0] as ContactFilterRemovedReasonInterface
              return (
                <FormSelect
                  key={'removed_reason_type-filter'}
                  label={
                    <span className={'font-inter text-text-primary text-sm font-bold'}>
                      {t('dashboard.contacts_bin.removed_reason_type')}
                    </span>
                  }
                  data={
                    isRootAdmin ? ContactRemovedTypeDataForRootAdmin : ContactRemovedTypeDataForUser
                  }
                  value={contactRemovedReasonType.searchValue}
                  onChange={reasonType =>
                    onChange([
                      {
                        searchValue: reasonType,
                      },
                    ])
                  }
                />
              )
            },
          },
        ],
      }}
    >
      <div className={'w-full'}>
        {removedContacts && removedContacts.items.length !== 0 && (
          <div
            className={
              'bg-brand-primary-subtle flex items-center gap-3 rounded-t-lg px-6 py-4 lg:hidden'
            }
          >
            {renderSelectAllButton()}
            <span>{t('global.select_all')}</span>
          </div>
        )}
        <Table
          data={removedContacts}
          columns={columns}
          limiter={{ limit, setLimit }}
          paginator={{ pagination, setPagination }}
        />
      </div>
    </ListingLayout>
  )
}

export const getServerSideProps = getBaseServerSideProps(ContactsLoco, 'header.top_menu.contacts')

export default Contacts
